import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Cropper from "react-easy-crop";
import {
    adminCreateArticle,
    adminUpdateArticle,
    adminGetArticle,
    adminUpload
} from "../api/adminCommunity";
import { API_BASE } from "../config";

const CATS = ["EVENT", "DJ", "AFFILIATE"];
const EXCERPT_MAX = 300;
const META_TITLE_MAX = 60;
const META_DESC_MAX = 160;
const OUTPUT_W = 1280;
const OUTPUT_H = 720;
const OUTPUT_ASPECT = OUTPUT_W / OUTPUT_H; // 16:9

const Badge = ({ children, tone = "default" }) => {
    const cls =
        tone === "success"
            ? "bg-emerald-500/20 text-emerald-200"
            : tone === "warning"
                ? "bg-amber-500/20 text-amber-200"
                : "bg-white/10 text-slate-200";
    return <span className={`badge ${cls}`}>{children}</span>;
};

// --- utilities for cropping ---
async function createImage(url) {
    return new Promise((resolve, reject) => {
        const img = new Image();
        img.addEventListener("load", () => resolve(img));
        img.addEventListener("error", (e) => reject(e));
        img.setAttribute("crossOrigin", "anonymous");
        img.src = url;
    });
}

async function exportWholeImageWithBlurBG(imageSrc, outW = OUTPUT_W, outH = OUTPUT_H) {
    const img = await createImage(imageSrc);
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    canvas.width = outW;
    canvas.height = outH;

    // 1) draw blurred background as a soft fill
    const bgScale = Math.max(outW / img.width, outH / img.height); // cover
    const bgW = img.width * bgScale;
    const bgH = img.height * bgScale;
    const bgX = (outW - bgW) / 2;
    const bgY = (outH - bgH) / 2;
    ctx.filter = 'blur(24px) brightness(0.9)'; // subtle blur + dim
    ctx.drawImage(img, bgX, bgY, bgW, bgH);
    ctx.filter = 'none';

    // 2) draw the *whole* image contained on top (no crop)
    const fgScale = Math.min(outW / img.width, outH / img.height); // contain
    const fgW = img.width * fgScale;
    const fgH = img.height * fgScale;
    const fgX = (outW - fgW) / 2;
    const fgY = (outH - fgH) / 2;
    ctx.drawImage(img, fgX, fgY, fgW, fgH);

    return new Promise((resolve, reject) => {
        canvas.toBlob(b => (b ? resolve(b) : reject(new Error('Canvas export failed'))), 'image/jpeg', 0.9);
    });
}

function getRadianAngle(degreeValue) {
    return (degreeValue * Math.PI) / 180;
}

async function getCroppedImgFromPixels(imageSrc, pixelCrop, outputW = OUTPUT_W, outputH = OUTPUT_H) {
    const image = await createImage(imageSrc);
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");
    canvas.width = outputW;
    canvas.height = outputH;
    // Draw the exact pixel rectangle from the source to the output size
    ctx.drawImage(
        image,
        Math.max(0, pixelCrop.x),
        Math.max(0, pixelCrop.y),
        Math.max(1, pixelCrop.width),
        Math.max(1, pixelCrop.height),
        0,
        0,
        outputW,
        outputH
    );
    return new Promise((resolve, reject) => {
        canvas.toBlob((blob) => {
            if (!blob) return reject(new Error("Canvas export failed"));
            resolve(blob);
        }, "image/jpeg", 0.92);
    });
}

export default function AdminArticleEdit() {
    const { id } = useParams(); // "new" or numeric id
    const navigate = useNavigate();
    const isNew = id === "new";

    const [events, setEvents] = useState([]);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [articleStatus, setArticleStatus] = useState("DRAFT");

    const [form, setForm] = useState({
        title: "",
        excerpt: "",
        bodyHtml: "",
        category: "EVENT",
        thumbnailUrl: "",
        author: "Hey Lets Travel",
        metaTitle: "",
        metaDescription: "",
        keywords: "",
        eventId: ""
    });

    // cropper modal state
    const fileInputRef = useRef(null);
    const [cropperOpen, setCropperOpen] = useState(false);
    const [rawImageUrl, setRawImageUrl] = useState(""); // object URL
    const [crop, setCrop] = useState({ x: 0, y: 0 });
    const [zoom, setZoom] = useState(1);
    const [rotation] = useState(0);
    const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);

    // Derived counters
    const counts = useMemo(
        () => ({
            excerpt: (form.excerpt || "").length,
            metaTitle: (form.metaTitle || "").length,
            metaDescription: (form.metaDescription || "").length
        }),
        [form]
    );

    function loadImageMeta(url) {
        return new Promise((resolve, reject) => {
            const img = new Image();
            img.onload = () => resolve({ width: img.naturalWidth, height: img.naturalHeight });
            img.onerror = reject;
            img.src = url;
        });
    }


    // Fetch events (light list)
    useEffect(() => {
        fetch(`${API_BASE}/api/events/list`)
            .then(r => r.json())
            .then(setEvents)
            .catch(console.error);
    }, []);

    // Load existing article
    useEffect(() => {
        if (isNew) return;
        (async () => {
            setLoading(true);
            try {
                const a = await adminGetArticle(Number(id));
                setForm({
                    title: a.title ?? "",
                    excerpt: a.excerpt ?? "",
                    bodyHtml: a.bodyHtml ?? "",
                    category: a.category ?? "EVENT",
                    thumbnailUrl: a.thumbnailUrl ?? "",
                    author: a.author ?? "Hey Lets Travel",
                    metaTitle: a.metaTitle ?? "",
                    metaDescription: a.metaDescription ?? "",
                    keywords: a.keywords ?? "",
                    eventId: a.eventId ?? "" // fixed: was eventsId
                });
                setArticleStatus(a.publishedAt ? "PUBLISHED" : "DRAFT");
            } catch (e) {
                console.error(e);
            } finally {
                setLoading(false);
            }
        })();
    }, [id, isNew]);

    const onChange = (e) => {
        const { name, value, type, checked } = e.target;
        setForm(f => ({ ...f, [name]: type === "checkbox" ? checked : value }));
    };

    const save = async () => {
        setSaving(true);
        try {
            const payload = {
                ...form,
                category: String(form.category).toUpperCase().trim()
            };
            if (isNew) {
                await adminCreateArticle(payload);
            } else {
                await adminUpdateArticle(Number(id), payload);
            }
            navigate("/admin/articles");
        } catch (e) {
            alert(e?.message || "Failed to save article");
        } finally {
            setSaving(false);
        }
    };

    const openFilePicker = () => fileInputRef.current?.click();

    const onFileChosen = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        const url = URL.createObjectURL(file);
        try {
            const { width, height } = await loadImageMeta(url);
            const aspect = width / height;
            const closeTo169 = Math.abs(aspect - OUTPUT_ASPECT) < 0.02; // within ~2%
            if (closeTo169) {
                // Already 16:9 → scale exactly to OUTPUT and upload, no crop modal
                const blob = await exportWholeImageWithBlurBG(url, OUTPUT_W, OUTPUT_H); // keeps whole image
                const fileName = `article-${Date.now()}.jpg`;
                const ready = new File([blob], fileName, { type: 'image/jpeg' });
                const { publicUrl } = await adminUpload(ready, 'articles');
                setForm(f => ({ ...f, thumbnailUrl: publicUrl }));
                URL.revokeObjectURL(url);
                e.target.value = '';
                return;
            }
            // Not 16:9 → open cropper, default to show whole image
            setRawImageUrl(url);
            setCrop({ x: 0, y: 0 });
            setZoom(1);
            setCropperOpen(true);
        } catch {
            // fallback: open cropper
            setRawImageUrl(url);
            setCrop({ x: 0, y: 0 });
            setZoom(1);
            setCropperOpen(true);
        }
        // allow re-picking the same file later
        e.target.value = "";
    };

    const handleCropCancel = () => {
        setCropperOpen(false);
        // revoke object URL to avoid memory leaks
        if (rawImageUrl) URL.revokeObjectURL(rawImageUrl);
        setRawImageUrl("");
    };

    const handleCropConfirm = useCallback(async () => {
        try {
            if (!croppedAreaPixels) {
                alert("Crop not ready yet. Try again.");
                return;
            }
            const blob = await getCroppedImgFromPixels(rawImageUrl, croppedAreaPixels, OUTPUT_W, OUTPUT_H);
            // upload using existing API
            const fileName = `article-${Date.now()}.jpg`;
            const croppedFile = new File([blob], fileName, { type: "image/jpeg" });
            const { publicUrl } = await adminUpload(croppedFile, "articles");
            setForm(f => ({ ...f, thumbnailUrl: publicUrl }));
            setCropperOpen(false);
        } catch (err) {
            console.error(err);
            alert("Failed to crop or upload image.");
        } finally {
            if (rawImageUrl) URL.revokeObjectURL(rawImageUrl);
            setRawImageUrl("");
        }
    }, [rawImageUrl, crop, zoom]);

    return (
        <div className="container py-6 pb-24 space-y-4">
            {/* Header */}
            <header className="card">
                <div className="md:flex md:items-center md:justify-between gap-4">
                    <div>
                        <h1 className="font-display text-3xl md:text-4xl">
                            {isNew ? "New Article" : "Edit Article"}
                        </h1>
                        <p className="mt-1 text-slate-300">
                            Write content for the Community section. Add a clear title, thumbnail, and body HTML.
                        </p>
                    </div>
                    <Badge tone={articleStatus === "PUBLISHED" ? "success" : "warning"}>
                        {articleStatus}
                    </Badge>
                </div>
            </header>

            {/* Content Card */}
            <section className="card">
                <h2 className="text-white font-semibold text-lg">Content</h2>
                <p className="text-slate-300 text-sm mb-4">
                    Core content shown on the article page. Category and (optional) event determine how it appears in lists.
                </p>

                <div className="grid gap-4 md:grid-cols-2">
                    <div className="space-y-3">
                        {/* Title */}
                        <label className="block">
                            <div className="label">Title *</div>
                            <input
                                name="title"
                                className="input w-full"
                                value={form.title}
                                onChange={onChange}
                                required
                                placeholder="e.g., Summer House Festival Announced"
                            />
                            <p className="mt-1 text-xs text-slate-400">Use a concise, descriptive headline.</p>
                        </label>

                        {/* Category */}
                        <label className="block">
                            <div className="label">Category *</div>
                            <select
                                name="category"
                                className="input w-full"
                                value={form.category}
                                onChange={onChange}
                            >
                                {CATS.map(c => <option key={c} value={c}>{c}</option>)}
                            </select>
                            <p className="mt-1 text-xs text-slate-400">Choose where this article is listed in Community.</p>
                        </label>

                        {/* Thumbnail (with cropper) */}
                        <label className="block">
                            <div className="label">Thumbnail</div>

                            <div className="flex items-center gap-3">
                                <input
                                    name="thumbnailUrl"
                                    className="input w-full"
                                    value={form.thumbnailUrl}
                                    onChange={onChange}
                                    placeholder="Paste a URL or use Upload & Crop…"
                                />

                                <input
                                    ref={fileInputRef}
                                    type="file"
                                    accept="image/*"
                                    className="hidden"
                                    onChange={onFileChosen}
                                />
                                <button type="button" className="btn-secondary" onClick={openFilePicker}>
                                    Upload &amp; Crop
                                </button>

                                {form.thumbnailUrl && (
                                    <button
                                        type="button"
                                        className="btn-ghost"
                                        onClick={() => setForm(f => ({ ...f, thumbnailUrl: "" }))}
                                    >
                                        Remove
                                    </button>
                                )}
                            </div>

                            {form.thumbnailUrl && (
                                <div className="mt-2">
                                    {/* Show final 16:9 preview at a decent size */}
                                    <div className="relative aspect-video max-w-[360px] overflow-hidden rounded-lg border border-white/10 bg-white/5">
                                        <img
                                            src={form.thumbnailUrl}
                                            alt="thumbnail preview"
                                            className="absolute inset-0 h-full w-full object-cover"
                                        />
                                    </div>
                                    <div className="mt-1 text-xs text-slate-400">Final crop (16:9 at 1280×720 uploaded)</div>
                                </div>
                            )}

                            <p className="mt-1 text-xs text-slate-400">
                                Recommended 16:9 image, at least 1280×720. Use <em>Upload &amp; Crop</em> to frame the subject.
                            </p>
                        </label>

                        {/* Author */}
                        <label className="block">
                            <div className="label">Author</div>
                            <input
                                name="author"
                                className="input w-full"
                                value={form.author}
                                onChange={onChange}
                                placeholder="Byline shown on article page"
                            />
                        </label>

                        {/* Event Link */}
                        <label className="block">
                            <div className="label">Link to Event (optional)</div>
                            <select
                                name="eventId"
                                className="input w-full"
                                value={form.eventId ?? ""}
                                onChange={onChange}
                            >
                                <option value="">— No event —</option>
                                {events.map(e => (
                                    <option key={e.id} value={e.id}>
                                        {e.title}
                                    </option>
                                ))}
                            </select>
                            <p className="mt-1 text-xs text-slate-400">
                                If set, an action button may appear (e.g., “Purchase tickets”).
                            </p>
                        </label>
                    </div>

                    <div className="space-y-3">
                        {/* Excerpt */}
                        <label className="block">
                            <div className="label">Excerpt (≤ {EXCERPT_MAX})</div>
                            <textarea
                                name="excerpt"
                                className="input w-full"
                                rows={3}
                                maxLength={EXCERPT_MAX}
                                value={form.excerpt}
                                onChange={onChange}
                                placeholder="Short summary used in lists and social previews"
                            />
                            <div className="mt-1 text-xs text-slate-400 flex justify-between">
                                <span>Keep it scannable and specific.</span>
                                <span>{counts.excerpt}/{EXCERPT_MAX}</span>
                            </div>
                        </label>

                        {/* Body HTML */}
                        <label className="block">
                            <div className="label">Body HTML</div>
                            <textarea
                                name="bodyHtml"
                                className="input w-full font-mono"
                                rows={16}
                                value={form.bodyHtml}
                                onChange={onChange}
                                placeholder="Paste HTML from your editor"
                            />
                            <p className="mt-1 text-xs text-slate-400">
                                Full article content. Supports standard HTML elements.
                            </p>
                        </label>
                    </div>
                </div>
            </section>

            {/* SEO Card */}
            <section className="card">
                <h2 className="text-white font-semibold text-lg">SEO & Metadata</h2>
                <p className="text-slate-300 text-sm mb-4">
                    Improve how your article appears on Google and social platforms.
                </p>

                <div className="grid md:grid-cols-2 gap-3">
                    <label className="block">
                        <div className="label">Meta Title (≤ {META_TITLE_MAX})</div>
                        <input
                            name="metaTitle"
                            className="input w-full"
                            maxLength={META_TITLE_MAX}
                            value={form.metaTitle}
                            onChange={onChange}
                            placeholder="Usually similar to your article title"
                        />
                        <div className="mt-1 text-xs text-slate-400 text-right">
                            {counts.metaTitle}/{META_TITLE_MAX}
                        </div>
                    </label>

                    <label className="block">
                        <div className="label">Meta Description (≤ {META_DESC_MAX})</div>
                        <input
                            name="metaDescription"
                            className="input w-full"
                            maxLength={META_DESC_MAX}
                            value={form.metaDescription}
                            onChange={onChange}
                            placeholder="One-sentence summary shown in search results"
                        />
                        <div className="mt-1 text-xs text-slate-400 text-right">
                            {counts.metaDescription}/{META_DESC_MAX}
                        </div>
                    </label>
                </div>

                <label className="block mt-3">
                    <div className="label">Keywords (comma-separated)</div>
                    <input
                        name="keywords"
                        className="input w-full"
                        value={form.keywords}
                        onChange={onChange}
                        placeholder="Hey Let’s Travel, House Music, UK Events"
                    />
                    <p className="mt-1 text-xs text-slate-400">
                        Optional. Used for internal search or SEO depending on your setup.
                    </p>
                </label>
            </section>

            {/* Sticky actions */}
            <div className="fixed bottom-0 left-0 right-0 z-30 border-t border-white/10 bg-[var(--bg)]/80 backdrop-blur">
                <div className="container py-3 flex items-center justify-between">
                    <span className="text-slate-300 text-sm">
                        {isNew ? "Creating new article" : "Editing article"}
                    </span>
                    <div className="flex gap-2">
                        <button
                            className="btn-secondary"
                            onClick={() => navigate("/admin/articles")}
                            disabled={saving}
                        >
                            Cancel
                        </button>
                        <button
                            className="btn-primary"
                            onClick={save}
                            disabled={saving || loading || !form.title.trim()}
                            aria-busy={saving}
                        >
                            {saving ? "Saving…" : "Save"}
                        </button>
                    </div>
                </div>
            </div>

            {/* --- CROPPER MODAL --- */}
            {cropperOpen && (
                <div
                    className="fixed inset-0 z-50 grid place-items-center bg-black/70 p-4"
                    role="dialog"
                    aria-modal="true"
                    aria-label="Crop image to 16:9"
                >
                    <div className="w-full max-w-3xl rounded-2xl border border-white/10 bg-[var(--surface)] shadow-card">
                        <div className="p-4 border-b border-white/10">
                            <h3 className="text-white font-semibold">Crop thumbnail (16:9 — 1280×720)</h3>
                            <p className="text-sm text-slate-400 mt-1">
                                Drag to position. Use the slider to zoom. The result will be uploaded at 1280×720.
                            </p>
                        </div>

                        <div className="relative h-[55vh] min-h-[360px] bg-black">
                            {rawImageUrl && (
                                <Cropper
                                    image={rawImageUrl}
                                    aspect={OUTPUT_ASPECT}
                                    crop={crop}
                                    zoom={zoom}
                                    onCropChange={setCrop}
                                    onZoomChange={setZoom}
                                    onCropComplete={(area, areaPixels) => setCroppedAreaPixels(areaPixels)}
                                    cropShape="rect"
                                    showGrid={false}
                                    objectFit="contain"
                                    minZoom={1}
                                />
                            )}
                        </div>

                        <div className="p-4 border-t border-white/10 flex items-center justify-between gap-3">
                            <div className="flex items-center gap-3">
                                <label className="text-sm text-slate-300">
                                    Zoom
                                    <input
                                        type="range"
                                        min="1"
                                        max="3"
                                        step="0.01"
                                        value={zoom}
                                        onChange={(e) => setZoom(parseFloat(e.target.value))}
                                        className="ml-2 align-middle"
                                    />
                                </label>
                            </div>
                            <div className="flex items-center gap-2">
                                <button className="btn-secondary" onClick={handleCropCancel}>Cancel</button>
                                <button className="btn-primary" onClick={handleCropConfirm}>Crop &amp; Upload</button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
            {/* --- /CROPPER MODAL --- */}
        </div>
    );
}
